import React, { useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import notificationService from '../../../services/notificationService';

const CompatibilityInsights = ({ insights }) => {
  // Trigger notification for high-priority recommendations
  React.useEffect(() => {
    if (insights?.recommendations) {
      const highPriorityRecs = insights?.recommendations?.filter(rec => rec?.priority === 'high');
      if (highPriorityRecs?.length > 0) {
        const firstRec = highPriorityRecs?.[0];
        notificationService?.notifyAISuggestion(
          'compatibility_insight',
          'high',
          firstRec?.title || 'New high-priority relationship insight available'
        );
      }
    }
  }, [insights]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'primary';
    if (score >= 40) return 'warning';
    return 'accent';
  };

  const getScoreIcon = (score) => {
    if (score >= 80) return 'TrendingUp';
    if (score >= 60) return 'Activity';
    if (score >= 40) return 'AlertCircle';
    return 'TrendingDown';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 shadow-therapeutic">
      <div className="flex items-center space-x-3 mb-4 md:mb-6">
        <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-md">
          <Icon name="BarChart3" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
            Compatibility Insights
          </h2>
          <p className="text-sm md:text-base text-muted-foreground font-caption">
            AI-powered relationship analysis
          </p>
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {insights?.scores?.map((score) => (
            <div
              key={score?.id}
              className="bg-muted/30 border border-border rounded-lg p-4 md:p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={score?.icon} 
                    size={20} 
                    color={`var(--color-${getScoreColor(score?.value)})`}
                  />
                  <span className="text-sm md:text-base font-medium text-foreground">
                    {score?.category}
                  </span>
                </div>
                <span className={`text-lg md:text-xl font-heading font-bold text-${getScoreColor(score?.value)}`}>
                  {score?.value}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 md:h-2.5 overflow-hidden">
                <div
                  className={`h-full bg-${getScoreColor(score?.value)} transition-therapeutic`}
                  style={{ width: `${score?.value}%` }}
                />
              </div>
              <p className="text-xs md:text-sm text-muted-foreground mt-2 font-caption">
                {score?.description}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-5">
          <div className="flex items-start space-x-3">
            <Icon name="Sparkles" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-1" />
            <div>
              <p className="text-sm md:text-base font-medium text-foreground mb-2">
                Overall Compatibility
              </p>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-3">
                {insights?.overallSummary}
              </p>
              <div className="flex items-center space-x-2">
                <div className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 bg-${getScoreColor(insights?.overallScore)}/10 rounded-md`}>
                  <Icon 
                    name={getScoreIcon(insights?.overallScore)} 
                    size={18} 
                    color={`var(--color-${getScoreColor(insights?.overallScore)})`}
                  />
                </div>
                <span className={`text-2xl md:text-3xl font-heading font-bold text-${getScoreColor(insights?.overallScore)}`}>
                  {insights?.overallScore}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm md:text-base font-medium text-foreground mb-3">
            AI Recommendations
          </p>
          <div className="space-y-2 md:space-y-3">
            {insights?.recommendations?.map((rec, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-3 md:p-4 bg-muted/30 border border-border rounded-md"
              >
                <Icon 
                  name={rec?.icon} 
                  size={18} 
                  color={`var(--color-${rec?.priority === 'high' ? 'accent' : rec?.priority === 'medium' ? 'warning' : 'primary'})`}
                  className="flex-shrink-0 mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm md:text-base text-foreground font-medium mb-1">
                    {rec?.title}
                  </p>
                  <p className="text-sm md:text-base text-muted-foreground">
                    {rec?.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompatibilityInsights;